import { Connector } from "@web3-react/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InitializedConnector } from "./useInitializeConnector";
import { Web3ConnectionType } from "../types";
import { JsonRpcProviderV5Wrapper } from "ethers-v5-wrappers";
import { JsonRpcApiProvider } from "ethers";

export interface ConnectorState<T extends Connector = Connector> {
  type: Web3ConnectionType;
  connector: T;
  chainId?: number;
  accounts?: string[];
  account?: string;
  isActivating: boolean;
  isLoading: boolean;
  isActive: boolean;
  provider?: JsonRpcApiProvider;
  ENSNames?: (string | null | undefined)[];
  ENSName?: string | null;
}

export const useConnector = <T extends Connector>(
  { hooks, connector, type }: InitializedConnector<T>,
  onUpdate: (s: ConnectorState<T>) => void,
) => {
  const shouldDisconnect = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  const chainId = hooks.useChainId();
  const accounts = hooks.useAccounts();
  const isActivating = hooks.useIsActivating();
  const account = hooks.useAccount();
  const isActive = hooks.useIsActive();
  const provider = hooks.useProvider();
  const ENSNames = hooks.useENSNames(provider);
  const ENSName = hooks.useENSName(provider);

  const state = useMemo(() => {
    return {
      type,
      connector,
      chainId,
      accounts,
      isActive,
      isActivating,
      isLoading,
      account,
      provider: provider ? new JsonRpcProviderV5Wrapper(provider) : undefined,
      ENSNames,
      ENSName,
    };
  }, [
    type,
    chainId,
    JSON.stringify(accounts),
    isActivating,
    account,
    isActive,
    isLoading,
    provider,
    JSON.stringify(ENSNames),
    ENSName,
  ]);

  shouldDisconnect.current = isActivating || isActive;

  const activate = useCallback(async () => {
    if (shouldDisconnect.current) {
      return;
    }

    shouldDisconnect.current = true;
    try {
      await connector.activate();
    } catch (e) {
      console.error("activate failed for " + type + " " + e);
      console.error(e);
      shouldDisconnect.current = false;
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    activate();
    return () => {
      if (shouldDisconnect.current) {
        connector.deactivate?.();
      }
    };
  }, [activate]);

  useEffect(() => {
    onUpdate(state);
  }, [state, onUpdate]);

  return state;
};
